<template>
  <v-container>
    <div class="d-flex align-center flex-wrap mb-1">
      <v-btn v-if="playSlug" text small class="mr-2" @click="showList" data-cy="macro-back">
        <v-icon left small>mdi-arrow-left</v-icon>
        All macros
      </v-btn>
      <h2>Print Macros</h2>
      <v-spacer/>
      <!-- the simple view (PlayMacros) only plays: nothing to import, export or record -->
      <template v-if="!simple">
      <v-btn v-if="!readOnly" text small @click="$refs.importInput.click()">
        <v-icon left small>mdi-upload</v-icon>
        Import json
      </v-btn>
      <input ref="importInput" type="file" accept="application/json,.json" class="d-none" @change="importFile"/>
      <v-btn text small :disabled="macros.length === 0" @click="exportMacros(macros, 'print-macros.json')">
        <v-icon left small>mdi-download</v-icon>
        Export all
      </v-btn>
      <v-btn
        v-if="!readOnly"
        color="red"
        dark
        small
        class="ml-2"
        :disabled="isRecording"
        @click="showRecordDialog = true"
        data-cy="macro-record-open"
      >
        <v-icon left small>mdi-record</v-icon>
        Record new macro
      </v-btn>
      </template>
    </div>

    <v-alert v-if="readOnly && !simple" type="info" text dense data-cy="macro-read-only">
      You have read-only access to print macros: you can run them, but not record or change them.
    </v-alert>
    <v-alert v-if="isRecording" type="error" text dense>
      A recording is in progress - use the panel at the bottom left of the screen to capture pages,
      then finish or cancel it there.
    </v-alert>

    <!-- choose a macro -->
    <template v-if="!playSlug">
      <p v-if="simple" class="grey--text text--darken-1" data-cy="macro-starred-note">
        Only the starred macros are shown here. Pick one and choose the production days to print for.
        Macros are starred on the Print Macros page.
      </p>
      <p v-else class="grey--text text--darken-1">
        Pick a macro, choose the production days to print for, and every page it recorded is downloaded as a pdf.
        Each page keeps the production or shipping group it was recorded with; only the dates change.
      </p>
      <v-progress-linear v-if="loading" indeterminate class="mb-2" data-cy="macro-loading"/>
      <v-alert v-if="loadError" dense type="error" text>could not load the macros: {{ loadError }}</v-alert>
      <v-alert v-if="!loading && !loadError && macros.length === 0" text type="info">
        No macros yet.
        <template v-if="!readOnly">Record one with the button above, or import a json export from another computer.</template>
        Macros are kept in this browser.
      </v-alert>
      <v-alert v-else-if="simple && !loading && listedMacros.length === 0" text type="info" data-cy="macro-none-starred">
        No macros are starred yet. Star the ones to play here on the Print Macros page.
      </v-alert>
      <template v-else>
        <div v-if="!simple" class="d-flex align-center flex-wrap mb-2">
          <v-checkbox
            :input-value="starredOnly"
            @change="setStarredOnly"
            label="Starred only"
            hide-details
            dense
            class="mt-0"
            data-cy="macro-starred-only"
          />
          <span v-if="hiddenCount > 0" class="caption grey--text ml-4" data-cy="macro-hidden-count">
            {{ hiddenCount }} macro{{ hiddenCount === 1 ? '' : 's' }} hidden - untick to see {{ hiddenCount === 1 ? 'it' : 'them' }}
          </span>
        </div>
        <v-alert v-if="!simple && starredOnly && starredCount === 0" text dense type="info" data-cy="macro-none-starred">
          No macros are starred yet. Click the star next to a macro to keep it in the short list; until then
          every macro is shown.
        </v-alert>
        <v-card outlined>
          <v-list data-cy="macro-list">
            <v-list-item
              v-for="macro of listedMacros"
              v-bind:key="macro.id"
              :data-cy="`macro-${macro.id}`"
              @click="selectMacro(macro)"
            >
              <v-list-item-action v-if="!simple" class="mr-3">
                <v-btn icon :color="macro.starred ? 'amber darken-2' : 'grey'" @click.stop="toggleStar(macro)"
                       :disabled="readOnly" :title="macro.starred ? 'unstar' : 'star'" data-cy="macro-star">
                  <v-icon>{{ macro.starred ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                </v-btn>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ macro.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ macro.steps.length }} page{{ macro.steps.length === 1 ? '' : 's' }}
                  <template v-if="macroGroups(macro).length"> &middot; {{ macroGroups(macro).join(', ') }}</template>
                  &middot; {{ macro.orientation }}
                  &middot; {{ savedBy(macro) }}
                  <template v-if="describeSchedule(macro.schedule)">
                    <br/>
                    <span class="primary--text" data-cy="macro-list-schedule">
                      <v-icon x-small color="primary">mdi-clock-check-outline</v-icon>
                      scheduled {{ describeSchedule(macro.schedule) }}<template v-if="nextRunOf(macro)">, next {{ formatRunTime(nextRunOf(macro).at) }}</template>
                    </span>
                  </template>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-play</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </template>
    </template>

    <!-- play the chosen macro -->
    <v-alert v-else-if="!selectedMacro" type="warning" text data-cy="macro-missing">
      <template v-if="simple">There is no starred macro called "{{ playSlug }}". Go back to the list to pick one.</template>
      <template v-else>There is no macro called "{{ playSlug }}". Go back to the list to pick one, or import it.</template>
    </v-alert>
    <template v-else>
      <v-card outlined class="mb-4" data-cy="macro-play">
        <v-card-title>
          <v-btn v-if="!simple" icon :color="selectedMacro.starred ? 'amber darken-2' : 'grey'" class="mr-1"
                 @click="toggleStar(selectedMacro)" :disabled="readOnly"
                 :title="selectedMacro.starred ? 'unstar' : 'star'" data-cy="macro-star">
            <v-icon>{{ selectedMacro.starred ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
          </v-btn>
          {{ selectedMacro.name }}
          <span class="grey--text ml-3 body-2">
            {{ selectedMacro.steps.length }} page{{ selectedMacro.steps.length === 1 ? '' : 's' }}
            <template v-if="macroGroups(selectedMacro).length"> &middot; {{ macroGroups(selectedMacro).join(', ') }}</template>
            <template v-if="isCompleteTarget(selectedMacro.baseline)">
              &middot; recorded for {{ describeTarget(selectedMacro.baseline) }}
            </template>
            &middot; {{ selectedMacro.orientation }}
          </span>
          <v-spacer/>
          <v-btn icon small :href="bookmarkHref" title="link to this macro (right click to bookmark)">
            <v-icon small>mdi-bookmark-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle data-cy="macro-saved-by">{{ savedBy(selectedMacro) }}</v-card-subtitle>
        <!-- the macro's settings and schedule, changed in the editor below (Edit); a line each -->
        <v-alert v-if="!simple" :color="scheduleOn ? 'primary' : 'grey'" text dense class="mx-4 mb-2" data-cy="macro-schedule-card">
          <div class="d-flex align-start flex-wrap">
            <div class="flex-grow-1">
              <div data-cy="macro-settings">
                <v-icon small class="mr-1">mdi-tune</v-icon>
                {{ orientationText }} &middot;
                <template v-if="selectedMacro.delivery === 'slack'">pdfs post to Slack {{ macroSlackChannelName }}</template>
                <template v-else>pdfs download here</template>
                <template v-if="openAfterSave"> &middot; each pdf opens after saving (this browser)</template>
                <template v-if="pageCss"> &middot; page sizes from each page's own css (this browser)</template>
              </div>
              <div class="mt-1">
                <v-icon small :color="scheduleOn ? 'primary' : 'grey'" class="mr-1">{{ scheduleOn ? 'mdi-clock-check-outline' : 'mdi-clock-outline' }}</v-icon>
                <span v-if="scheduleOn" data-cy="macro-schedule">
                  <strong>Runs on a schedule:</strong> {{ describeSchedule(selectedMacro.schedule) }}<template v-if="selectedMacro.schedule.runAs">, as {{ selectedMacro.schedule.runAs.email }}</template>.
                  <template v-if="nextScheduledRun">Next run {{ formatRunTime(nextScheduledRun.at) }} &rarr; {{ formatRunDays(nextScheduledRun.dates) }}.</template>
                </span>
                <span v-else data-cy="macro-schedule-none">Not on a schedule.</span>
              </div>
            </div>
            <v-btn v-if="!readOnly && !draft" text small color="primary" class="ml-2" @click="startEditing" data-cy="macro-schedule-edit">
              <v-icon left small>mdi-pencil</v-icon>
              Edit settings
            </v-btn>
          </div>
        </v-alert>
        <v-card-text>
          <v-row>
            <!-- what to run: the button asks for the production days in a dialog and runs once they are chosen;
                 a phone gets it above the progress -->
            <v-col cols="12" md="6">
              <ProductionDaysSelector
                ref="datesSelector"
                :dates="target.dates"
                hide-input
                @update="datesChosen"
                @close="runWhenDatesChosen = false"
              />
              <div class="caption grey--text">
                Run macro asks for the production days to print for; each page keeps its own production or shipping group
                <template v-if="macroGroups(selectedMacro).length">({{ macroGroups(selectedMacro).join(', ') }})</template>.
              </div>
              <div class="d-flex align-center flex-wrap mt-3">
                <span v-if="runBlockedReason" class="caption grey--text mr-3">{{ runBlockedReason }}</span>
                <v-spacer/>
                <v-btn
                  color="primary"
                  :disabled="!!runBlockedReason || serverRendering || isRecording || draft !== null"
                  @click="replay(selectedMacro)"
                  data-cy="macro-run"
                >
                  <v-icon left>mdi-play</v-icon>
                  Run macro
                </v-btn>
                <v-btn
                  v-if="!simple && !readOnly"
                  outlined
                  color="primary"
                  class="ml-2"
                  :disabled="draft !== null"
                  @click="openScheduleDialog"
                  data-cy="macro-schedule-open"
                >
                  <v-icon left>mdi-clock-outline</v-icon>
                  Run on schedule
                </v-btn>
              </div>
              <v-alert v-if="replayError" dense type="error" class="mt-2 mb-0">{{ replayError }}</v-alert>
            </v-col>
            <!-- the run: a render on the server, the pages printed by chrome there and downloaded here as they finish -->
            <v-col cols="12" md="6">
              <v-card v-if="serverRender" outlined data-cy="macro-server-render">
                <v-card-subtitle class="pb-1">
                  <v-icon small :color="serverRendering ? 'primary' : (serverRender.status === 'done' ? 'success' : 'orange')" class="mr-1">
                    {{ serverRendering ? 'mdi-server' : (serverRender.status === 'done' ? 'mdi-check-circle' : 'mdi-alert-circle') }}
                  </v-icon>
                  Server render {{ serverRender.status === 'delivering' ? 'posting to Slack' : serverRender.status }}<template v-if="serverRender.error">: {{ serverRender.error }}</template>
                  <template v-if="serverRendering && serverRender.current >= 0">
                    &middot; page {{ serverRender.current + 1 }} of {{ serverRender.steps.length }}
                  </template>
                  <template v-if="serverStats"> &middot; {{ serverStats }}</template>
                  <v-btn v-if="serverRendering" text x-small color="error" class="ml-2" @click="cancelServerRender" data-cy="macro-server-cancel">Stop</v-btn>
                  <template v-else>
                    <v-btn v-if="!simple && slack.configured && serverHasPdfs && serverRender.delivery !== 'slack'" text x-small color="primary" class="ml-2"
                           :loading="slackSending" :disabled="slackSending || !playOptions.slackChannel"
                           :title="playOptions.slackChannel ? '' : 'choose a Slack channel above first'"
                           @click="sendServerRenderToSlack" data-cy="macro-server-slack">
                      <v-icon x-small left>mdi-slack</v-icon>
                      <template v-if="!playOptions.slackChannel">Send to Slack</template>
                      <template v-else-if="serverRender.slack && serverRender.slack.status === 'sent'">Send to Slack {{ playOptions.slackChannel.name }} again</template>
                      <template v-else>Send to Slack {{ playOptions.slackChannel.name }}</template>
                    </v-btn>
                    <v-btn text x-small class="ml-2" @click="dismissServerRender">Dismiss</v-btn>
                  </template>
                </v-card-subtitle>
                <!-- the pdfs on slack: posted when the render finished (macro option) or by the button; each pdf's link is on its page below -->
                <v-card-subtitle v-if="serverRender.slack" class="pt-0 pb-1" data-cy="macro-server-slack-status">
                  <v-icon small :color="{sent: 'success', failed: 'error'}[serverRender.slack.status] || 'primary'" class="mr-1">
                    {{ {sent: 'mdi-check-circle', failed: 'mdi-alert-circle'}[serverRender.slack.status] || 'mdi-progress-upload' }}
                  </v-icon>
                  <template v-if="serverRender.slack.status === 'sent'">
                    {{ serverRender.delivery === 'slack' ? 'Posted' : 'Sent' }} {{ serverRender.slack.files.length }} pdf{{ serverRender.slack.files.length === 1 ? '' : 's' }}
                    to Slack {{ serverRender.slack.channel }}
                  </template>
                  <template v-else-if="serverRender.slack.status === 'failed'">
                    could not send to Slack: {{ serverRender.slack.error }}
                  </template>
                  <template v-else>sending to Slack {{ serverRender.slack.channel }}...</template>
                </v-card-subtitle>
                <v-progress-linear :value="serverProgress" :indeterminate="serverRender.status === 'queued'" height="4"/>
                <v-list dense class="py-0">
                  <v-list-item v-for="step of serverRender.steps" v-bind:key="step.index" class="px-4">
                    <v-list-item-icon class="my-1 mr-2">
                      <v-icon small :color="serverStepColor(step.status)">{{ serverStepIcon(step.status) }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="py-1">
                      <v-list-item-title>{{ step.filename }}</v-list-item-title>
                      <v-list-item-subtitle v-if="step.error" class="red--text text-wrap">{{ step.error }}</v-list-item-subtitle>
                      <v-list-item-subtitle v-else-if="step.status === 'done-unsettled'" class="orange--text text-wrap">
                        page was still loading when printed: {{ (step.reasons || []).join(', ') }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-if="serverStepStats(step)" class="grey--text" data-cy="macro-server-stats">
                        {{ serverStepStats(step) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="my-0 flex-row align-center">
                      <v-btn icon small :href="pageHref(step.href)" target="_blank" title="open the page this was printed from"
                             data-cy="macro-server-open-page">
                        <v-icon small>mdi-link-variant</v-icon>
                      </v-btn>
                      <v-btn v-if="serverPdfLink(step)" icon small :href="serverPdfLink(step).href" target="_blank"
                             :title="serverPdfLink(step).title" data-cy="macro-server-open-pdf">
                        <v-icon small>mdi-file-pdf-box</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card>
              <div v-else class="caption grey--text pt-2" data-cy="macro-run-placeholder">
                The run's progress and its pdfs appear here.
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- the pages, and editing them -->
      <v-card v-if="!simple" outlined data-cy="macro-details">
        <v-card-title class="subtitle-1">
          Pages
          <v-spacer/>
          <template v-if="draft">
            <v-btn text small @click="draft = null" data-cy="macro-edit-cancel">Cancel</v-btn>
            <v-btn color="primary" small @click="saveDraft" data-cy="macro-edit-save">
              <v-icon left small>mdi-content-save</v-icon>
              Save
            </v-btn>
          </template>
          <template v-else>
            <v-btn text small @click="exportMacros([selectedMacro], `print-macro-${slugify(selectedMacro.name)}.json`)">
              <v-icon left small>mdi-download</v-icon>
              Export
            </v-btn>
            <v-btn v-if="!readOnly" text small color="error" @click="confirmDelete(selectedMacro)" data-cy="macro-delete">
              <v-icon left small>mdi-delete</v-icon>
              Delete
            </v-btn>
            <v-btn v-if="!readOnly" text small color="primary" @click="startEditing" data-cy="macro-edit">
              <v-icon left small>mdi-pencil</v-icon>
              Edit
            </v-btn>
          </template>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="draftError" dense type="error" data-cy="macro-edit-error">{{ draftError }}</v-alert>

          <!-- editing: everything is a draft until Save -->
          <template v-if="draft">
            <v-row>
              <v-col cols="12" md="5">
                <v-text-field v-model="draft.name" label="Macro name" hide-details data-cy="macro-edit-name"/>
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model="draft.orientation" :items="orientations" label="PDF orientation" hide-details data-cy="macro-edit-orientation"/>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center flex-wrap macro-options">
                <v-select v-model="draft.delivery" :items="deliveries" label="Pdfs go to" hide-details :disabled="!slack.configured"
                          data-cy="macro-edit-delivery"/>
                <!-- kept on the macro while its pdfs download, but only asked for when they go to slack -->
                <SlackChannelPicker
                  v-if="slack.configured && draft.delivery === 'slack'"
                  v-model="draft.slackChannel"
                  :channels="knownSlackChannels"
                />
                <div v-if="!slack.configured" class="caption grey--text mt-1">the api has no Slack bot set up</div>
              </v-col>
            </v-row>
            <!-- when the macro runs by itself; the Run on schedule button beside Run macro edits the same -->
            <MacroScheduleFields :draft="draft" :channels="knownSlackChannels" :slack="slack"/>
            <!-- this browser's own settings for runs started here: not part of the macro, so they apply at once -->
            <div class="mt-4 subtitle-2">This browser</div>
            <v-checkbox
              :input-value="openAfterSave"
              @change="setOpenAfterSave"
              label="Open each pdf after saving"
              hide-details
              dense
              class="mt-1"
              data-cy="macro-open-after-save"
            />
            <!-- while the fixed page sizes are proven on the printers: off prints as before -->
            <v-checkbox
              :input-value="pageCss"
              @change="setPageCss"
              label="Page sizes from each page's own css (testing)"
              hide-details
              dense
              class="mt-1"
              data-cy="macro-page-css"
            />
            <div class="caption grey--text">
              On: the packing box stickers print one sticker per 7.6 x 12.5 cm page instead of on A4. These two apply
              at once to prints from this browser and to runs started here; they are not saved with the macro.
            </div>
            <p class="caption grey--text mt-3 mb-1">
              The url is the part of the address after <code>#</code>.
              <code>{{ tokens.dates }}</code> and <code>{{ tokens.date0 }}</code>, <code>{{ tokens.date1 }}</code>...
              are filled in from the production days chosen at replay; the production or shipping group in a url
              (local, ship, CC) stays as written.
            </p>
            <v-simple-table dense data-cy="macro-page-list">
              <thead>
              <tr>
                <th style="width: 2em">#</th>
                <th style="width: 18%">Page</th>
                <th>Url</th>
                <th style="width: 22%">Saves as</th>
                <th style="width: 6em"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(step, index) of draft.steps" v-bind:key="step.key">
                <td>{{ index + 1 }}</td>
                <td>
                  <v-text-field v-model="step.label" dense hide-details data-cy="macro-edit-label"/>
                </td>
                <td>
                  <v-text-field v-model="step.url" dense hide-details class="text-mono" data-cy="macro-edit-url"/>
                  <div v-for="warning of urlWarnings(step.url)" v-bind:key="warning" class="caption orange--text">
                    {{ warning }}
                  </div>
                </td>
                <td>
                  <v-text-field v-model="step.filename" dense hide-details suffix=".pdf" data-cy="macro-edit-filename"/>
                </td>
                <td class="text-right text-no-wrap">
                  <v-btn icon x-small :disabled="index === 0" @click="moveDraftStep(index, -1)" title="move up">
                    <v-icon small>mdi-arrow-up</v-icon>
                  </v-btn>
                  <v-btn icon x-small :disabled="index === draft.steps.length - 1" @click="moveDraftStep(index, 1)"
                         title="move down">
                    <v-icon small>mdi-arrow-down</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="draft.steps.splice(index, 1)" title="remove page">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
              </tbody>
            </v-simple-table>
          </template>

          <!-- viewing -->
          <v-simple-table v-else dense data-cy="macro-page-list">
            <thead>
            <tr>
              <th style="width: 2em">#</th>
              <th style="width: 18%">Page</th>
              <th>Url</th>
              <th style="width: 22%">Saves as</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(step, index) of selectedMacro.steps" v-bind:key="index">
              <td>{{ index + 1 }}</td>
              <td>{{ step.label }}</td>
              <td class="caption">
                <a v-if="isCompleteTarget(target)" :href="previewHref(step, target)" target="_blank">
                  {{ previewPath(step, target) }}
                </a>
                <span v-else class="grey--text">{{ stepUrl(step) }}</span>
                <div v-for="warning of step.warnings" v-bind:key="warning" class="orange--text">
                  {{ warning }}
                </div>
              </td>
              <td class="caption">
                {{ stepFilename(step, isCompleteTarget(target) ? target : selectedMacro.baseline) }}
              </td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </template>

    <v-alert text type="warning" class="mt-6" dense>
      Your browser may ask once to allow this site to download multiple files - allow it, or only the
      first pdf of a run will be saved. If the browser is set to ask where to save each download, a
      dialog will open for every page. Opening each pdf after saving needs pop-ups allowed for this site;
      the render list also has an Open button beside every saved pdf.
    </v-alert>

    <!-- run on schedule: the schedule (and the slack delivery it needs) saved on their own -->
    <v-dialog v-model="showScheduleDialog" max-width="640" data-cy="macro-schedule-dialog">
      <v-card v-if="scheduleDraft && selectedMacro">
        <v-card-title class="subtitle-1">Run "{{ selectedMacro.name }}" on a schedule</v-card-title>
        <v-card-text>
          <MacroScheduleFields :draft="scheduleDraft" :channels="knownSlackChannels" :slack="slack" show-delivery/>
          <v-alert v-if="scheduleDialogError" dense type="error" class="mt-3 mb-0" data-cy="macro-schedule-error">{{ scheduleDialogError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showScheduleDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="scheduleSaving" @click="saveScheduleDialog" data-cy="macro-schedule-save">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- record -->
    <v-dialog v-model="showRecordDialog" max-width="700" data-cy="macro-record-dialog">
      <v-card>
        <v-card-title>Record a new macro</v-card-title>
        <v-card-text>
          <ol class="mb-4">
            <li>Give the macro a name and start recording. Choosing the production days here is optional: if you
              leave them blank they are taken from the first page you capture that has dates selected.
            </li>
            <li>Open each page you print, select its production or shipping group and the dates, and wait for it to
              load. Each page keeps the group you chose; replaying only changes the dates.
            </li>
            <li>Click <em>Add this page</em> in the recording panel and name its pdf. Nothing is printed while
              recording: the pages are rendered on the server when the macro runs.
            </li>
            <li>Click <em>Finish</em> when you have added every page.</li>
          </ol>
          <v-text-field
            v-model="newMacro.name"
            label="Macro name"
            placeholder="e.g. Monday kitchen pack"
            data-cy="macro-name"
          />
          <ProductionDaysSelector
            :dates="newMacro.baseline.dates"
            @update="newMacro.baseline = $event"
          />
          <div class="caption grey--text px-2 mb-4">optional: the production days you are printing for</div>
          <v-select
            v-model="newMacro.orientation"
            :items="orientations"
            label="PDF orientation"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showRecordDialog = false">Cancel</v-btn>
          <v-btn color="red" dark @click="beginRecording" data-cy="macro-record">
            <v-icon left>mdi-record</v-icon>
            Start recording
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment/moment';
import {mapActions, mapGetters, mapState} from 'vuex';
import MacroScheduleFields from '@/components/MacroScheduleFields.vue';
import ProductionDaysSelector from '@/components/ProductionDaysSelector.vue';
import SlackChannelPicker from '@/components/SlackChannelPicker.vue';
import urlState from '@/router/urlState';
import {
  cleanFilename,
  describeTarget,
  isCompleteTarget,
  linksFilename,
  macroGroups,
  parseStepUrl,
  replayLinksText,
  resolveStep,
  serverRenderRequest,
  slugify,
  stepFilename,
  stepUrl,
  urlWarnings,
} from '@/utils/macro-steps';
import {downloadBlob, openInNewTab} from '@/utils/download';
import {browserMemoryUsed, pageStatsText} from '@/utils/format-stats';
import {cancelRender, fetchRenderedPdf, fetchSlackConfig, followRender, postRenderToSlack, startRender} from '@/utils/macro-render-client';
import {printPageCssEnabled, setPrintPageCssEnabled} from '@/utils/print-page';
import {cleanSchedule, describeSchedule, scheduleProblem, upcomingRuns} from '@/utils/macro-schedule';

const NO_TARGET = {dates: []};

/** the value of ?play= that opens a macro: its name as a slug (an id is accepted too) */
function macroPlaySlug(macro) {
  return slugify(macro.name);
}

export default {
  name: 'PrintMacros',
  components: {MacroScheduleFields, ProductionDaysSelector, SlackChannelPicker},
  mixins: [urlState],
  props: {
    // the PlayMacros page: the starred macros only, played with their saved options; only the production days can change
    simple: {type: Boolean, default: false},
  },
  data() {
    return {
      // the macro to play, from ?play=<name slug>; the production days are chosen each visit and never in the url
      playSlug: null,
      target: {...NO_TARGET},
      // Run macro was pressed: the production days picker is up, and the run starts on a choice
      runWhenDatesChosen: false,
      // an editable copy of the selected macro while editing; nothing is saved until Save
      draft: null,
      draftError: null,
      showRecordDialog: false,
      newMacro: {
        name: '',
        baseline: {dates: []},
        orientation: 'portrait',
      },
      tokens: {dates: '{{dates}}', date0: '{{date:0}}', date1: '{{date:1}}'},
      orientations: [
        {text: 'Portrait', value: 'portrait'},
        {text: 'Landscape', value: 'landscape'},
      ],
      replayError: null,
      // the Run on schedule dialog: a draft of the schedule and the slack delivery it needs
      showScheduleDialog: false,
      scheduleDraft: null,
      scheduleDialogError: null,
      scheduleSaving: false,
      // this browser's print-page toggle (utils/print-page.js)
      pageCss: printPageCssEnabled(),
      deliveries: [
        {text: 'Download here', value: 'download'},
        {text: 'Post to Slack', value: 'slack'},
      ],
      // whether the api can post pdfs to slack, and its default channel {name, id} (GET v2/print-macros/slack)
      slack: {configured: false, channel: null},
      slackSending: false,
      // the job on the server while rendering there: {id, status, current, steps: [...]}
      serverRender: null,
      // the poll following that job (macro-render-client followRender), stopped when the page goes away
      serverFollow: null,
      // blob url of each page's pdf once downloaded, by step index (null while the download is in flight)
      serverDownloaded: {},
      // pdfs "open after saving" could not open: the browser blocked the pop-up
      openBlocked: 0,
      // this browser's memory after the latest download, for the render stats
      clientMemoryBytes: null,
    };
  },
  async mounted() {
    this.syncToUrl({param: 'playSlug', urlParam: 'play', initFromRoute: true});
    this.loadSlackConfig();
    try {
      await this.fetchMacros();
    } catch (e) {
      this.notify(`could not load the print macros: ${e.message || e}`, 'error');
    }
  },
  beforeDestroy() {
    this.stopFollowing();
    this.releaseServerFiles();
  },
  computed: {
    ...mapState('macros', ['macros', 'openAfterSave', 'starredOnly', 'loading', 'loadError']),
    serverRendering() {
      return !!this.serverRender && ['queued', 'running', 'delivering'].includes(this.serverRender.status);
    },
    serverProgress() {
      if (!this.serverRender || this.serverRender.steps.length === 0) {
        return 0;
      }
      const finished = this.serverRender.steps.filter(s => !['queued', 'running'].includes(s.status)).length;
      // the last stretch of a slack delivery is the posting itself
      return this.serverRender.status === 'delivering' ? 100 : 100 * finished / this.serverRender.steps.length;
    },
    /** every channel some macro posts to, plus the api's default: typed once, picked everywhere */
    knownSlackChannels() {
      const channels = new Map();
      [...this.macros.map(m => m.slackChannel), this.slack.channel].filter(c => c && c.id).forEach((c) => {
        if (!channels.has(c.id)) {
          channels.set(c.id, {name: c.name, id: c.id});
        }
      });
      return [...channels.values()];
    },
    /** how a run is made: the macro's saved orientation and delivery (changed in the editor) */
    playOptions() {
      const macro = this.selectedMacro || {};
      return {orientation: macro.orientation || 'portrait', delivery: macro.delivery || 'download', slackChannel: macro.slackChannel || null};
    },
    orientationText() {
      const item = this.orientations.find(o => o.value === this.playOptions.orientation);
      return item ? item.text : this.playOptions.orientation;
    },
    macroSlackChannelName() {
      const channel = this.effectiveSlackChannel;
      return channel ? channel.name : 'no channel';
    },
    /** the channel a slack delivery would post to: the macro's, else the api's default */
    effectiveSlackChannel() {
      return this.playOptions.slackChannel || this.slack.channel || null;
    },
    /** why the run button is off, in words; '' when it can run */
    runBlockedReason() {
      if (!this.selectedMacro || this.selectedMacro.steps.length === 0) {
        return 'the macro has no pages';
      }
      if (this.playOptions.delivery === 'slack' && !this.effectiveSlackChannel) {
        return this.simple ? 'the macro posts to Slack but has no channel: set one on the Print Macros page' : 'choose a Slack channel first';
      }
      return '';
    },
    serverHasPdfs() {
      return !!this.serverRender && this.serverRender.steps.some(s => s.status.startsWith('done'));
    },
    /** time and memory of the whole server render: the api process, its chrome, and this browser */
    serverStats() {
      const job = this.serverRender;
      if (!job) {
        return '';
      }
      return pageStatsText({
        durationMs: job.durationMs,
        memory: [
          {label: 'server', bytes: job.memoryBytes},
          {label: 'chrome', bytes: job.browserMemoryBytes},
          {label: 'this browser', bytes: this.clientMemoryBytes},
        ],
      });
    },
    ...mapGetters('macros', ['isRecording']),
    /** the router marks the route read-only when the role can read but not write this page */
    readOnly() {
      return !!this.$route.meta.readOnly;
    },
    starredCount() {
      return this.macros.filter(m => m.starred).length;
    },
    /** the starred-only filter only applies once something is starred; the simple view lists nothing else */
    listedMacros() {
      if (this.simple) {
        return this.macros.filter(m => m.starred);
      }
      if (this.starredOnly && this.starredCount > 0) {
        return this.macros.filter(m => m.starred);
      }
      return this.macros;
    },
    hiddenCount() {
      return this.macros.length - this.listedMacros.length;
    },
    selectedMacro() {
      if (!this.playSlug) {
        return null;
      }
      const playable = this.simple ? this.listedMacros : this.macros;
      return playable.find(m => macroPlaySlug(m) === this.playSlug || m.id === this.playSlug) || null;
    },
    scheduleOn() {
      return !!(this.selectedMacro && this.selectedMacro.schedule && this.selectedMacro.schedule.enabled);
    },
    nextScheduledRun() {
      return this.scheduleOn ? upcomingRuns(this.selectedMacro.schedule, new Date(), 1)[0] || null : null;
    },
    bookmarkHref() {
      return this.$router.resolve({name: this.$route.name, query: {play: this.playSlug}}).href;
    },
  },
  watch: {
    selectedMacro(macro, previous) {
      this.replayError = null;
      if (!macro || (previous && previous.id === macro.id)) {
        return;
      }
      this.draft = null;
      this.draftError = null;
      this.runWhenDatesChosen = false;
    },
  },
  methods: {
    ...mapActions('macros', ['fetchMacros', 'startRecording', 'updateMacro', 'deleteMacro', 'importMacros', 'setOpenAfterSave', 'setStarred', 'setStarredOnly']),
    describeSchedule,
    describeTarget,
    isCompleteTarget,
    scheduleProblem,
    macroGroups,
    slugify,
    stepFilename,
    stepUrl,
    urlWarnings,
    notify(message, color = 'info') {
      this.$store.commit('showSnackbar', {message, color, timeout: 5000});
    },
    formatRunTime(date) {
      return moment(date).format('ddd MMM D HH:mm');
    },
    /** e.g. 'Mon Sep 28 to Wed Sep 30' */
    formatRunDays(dates) {
      return `${moment(dates[0]).format('ddd MMM D')} to ${moment(dates[dates.length - 1]).format('ddd MMM D')}`;
    },
    selectMacro(macro) {
      this.playSlug = macroPlaySlug(macro);
    },
    async toggleStar(macro) {
      if (this.readOnly || this.simple) {
        return;
      }
      try {
        await this.setStarred({id: macro.id, starred: !macro.starred});
      } catch (e) {
        this.notify(`could not star the macro: ${e.message || e}`, 'error');
      }
    },
    /** e.g. 'saved Sep 14, 2026 07:30 by tom@example.com' */
    savedBy(macro) {
      const when = macro.updatedAt ? moment(macro.updatedAt).format('MMM D, YYYY HH:mm') : 'unknown date';
      return `saved ${when} by ${macro.updatedBy || 'unknown'}`;
    },
    showList() {
      this.playSlug = null;
      this.draft = null;
    },
    /** the dialog opens ready to go: pdfs to slack (the api's default channel when the macro has none) and the schedule on */
    openScheduleDialog() {
      const macro = this.selectedMacro;
      const draft = {delivery: macro.delivery, slackChannel: macro.slackChannel, schedule: this.editableSchedule(macro)};
      if (this.slack.configured) {
        draft.delivery = 'slack';
        draft.slackChannel = draft.slackChannel || this.slack.channel || null;
        draft.schedule.enabled = draft.schedule.enabled || !!draft.slackChannel;
      }
      this.scheduleDraft = draft;
      this.scheduleDialogError = null;
      this.showScheduleDialog = true;
    },
    async saveScheduleDialog() {
      const draft = this.scheduleDraft;
      const problem = scheduleProblem(draft.schedule, draft);
      if (problem) {
        this.scheduleDialogError = `schedule: ${problem}`;
        return;
      }
      this.scheduleSaving = true;
      try {
        await this.updateMacro({id: this.selectedMacro.id, delivery: draft.delivery, slackChannel: draft.slackChannel, schedule: draft.schedule});
        this.showScheduleDialog = false;
        this.notify(draft.schedule.enabled ? `"${this.selectedMacro.name}" runs ${describeSchedule(draft.schedule)}` : `"${this.selectedMacro.name}" is off its schedule`, 'success');
      } catch (e) {
        this.scheduleDialogError = `could not save: ${(e.response && e.response.data && e.response.data.error) || e.message || e}`;
      } finally {
        this.scheduleSaving = false;
      }
    },
    /** a macro's schedule as a draft to edit: a copy, or a blank one */
    editableSchedule(macro) {
      return cleanSchedule(macro.schedule) || {enabled: false, days: [], time: '', production: '', runAs: null};
    },
    nextRunOf(macro) {
      return upcomingRuns(macro.schedule, new Date(), 1)[0] || null;
    },
    /** the production days were picked for a run: it starts now */
    datesChosen(target) {
      this.target = target;
      if (this.runWhenDatesChosen) {
        this.runWhenDatesChosen = false;
        this.run(this.selectedMacro);
      }
    },
    async beginRecording() {
      try {
        await this.startRecording(this.newMacro);
        this.showRecordDialog = false;
        this.notify('recording - open the first page you want to print', 'info');
        this.newMacro = {name: '', baseline: {dates: []}, orientation: 'portrait'};
      } catch (e) {
        alert(e.message);
      }
    },
    previewPath(step, target) {
      const {path, query} = resolveStep(step, target);
      return this.$router.resolve({path, query}).href.replace(/^#/, '');
    },
    previewHref(step, target) {
      const {path, query} = resolveStep(step, target);
      return this.$router.resolve({path, query}).href;
    },
    setPageCss(on) {
      setPrintPageCssEnabled(!!on);
      this.pageCss = printPageCssEnabled();
    },
    /** Run macro: asks which production days to print for, and runs once they are chosen (datesChosen) */
    replay() {
      this.replayError = null;
      this.runWhenDatesChosen = true;
      this.$refs.datesSelector.open();
    },
    async run(macro) {
      this.replayError = null;
      try {
        await this.startServerRender(macro);
      } catch (e) {
        this.replayError = (e.response && e.response.data && e.response.data.error) || e.message;
      }
    },
    /** the part of the kitchen url after # for a resolved step, which is what the server opens */
    hashHrefFor(location) {
      return this.$router.resolve(location).href.replace(/^#/, '');
    },
    async loadSlackConfig() {
      try {
        this.slack = await fetchSlackConfig();
      } catch (e) {
        console.warn('could not ask the api about slack', e);
      }
    },
    /** post the finished pdfs to the macro's slack channel; the api keeps the outcome on the job */
    async sendServerRenderToSlack() {
      const job = this.serverRender;
      const channel = this.playOptions.slackChannel;
      if (!job || !channel || this.slackSending) {
        return;
      }
      this.slackSending = true;
      this.serverRender = {...job, slack: {status: 'sending', channel: channel.name, files: [], error: null}};
      try {
        const data = await postRenderToSlack(job.id, channel);
        this.serverRender = data;
        this.notify(`sent ${data.slack.files.length} pdf${data.slack.files.length === 1 ? '' : 's'} to Slack ${data.slack.channel}`, 'success');
      } catch (e) {
        const failed = e.response && e.response.data && e.response.data.slack;
        const error = (e.response && e.response.data && (e.response.data.error || (failed && failed.error))) || e.message || `${e}`;
        this.serverRender = failed ? e.response.data : {...this.serverRender, slack: {status: 'failed', channel: channel.name, files: [], error}};
        this.notify(`could not send to Slack: ${error}`, 'error');
      } finally {
        this.slackSending = false;
      }
    },
    /** the full kitchen url of a page the server printed */
    pageHref(href) {
      return `${window.location.origin}${window.location.pathname}#${href}`;
    },
    async startServerRender(macro) {
      if (!isCompleteTarget(this.target)) {
        throw new Error('choose the production days to render for first');
      }
      // the options as chosen for this run (they may differ from the macro's saved defaults)
      const request = serverRenderRequest({
        ...macro, orientation: this.playOptions.orientation, delivery: this.playOptions.delivery, slackChannel: this.effectiveSlackChannel,
      }, this.target, this.hashHrefFor);
      const job = await startRender(request);
      this.stopFollowing();
      this.releaseServerFiles();
      this.openBlocked = 0;
      this.serverRender = job;
      this.clientMemoryBytes = browserMemoryUsed();
      this.serverRenderContext = {macro, target: {dates: [...this.target.dates]}};
      this.followServerRender();
    },
    /** keep the card up to date with the job, saving each pdf as the server finishes it */
    followServerRender() {
      const follow = followRender(this.serverRender.id, {
        onUpdate: (job) => {
          this.serverRender = job;
          this.downloadServerFiles();
        },
      });
      this.serverFollow = follow;
      follow.finished.then(() => {
        this.finishServerRender();
      }, (e) => {
        this.notify(`lost the server render: ${e.message || e}`, 'error');
        this.serverRender = {...this.serverRender, status: 'failed', error: e.message || `${e}`};
      });
    },
    stopFollowing() {
      if (this.serverFollow) {
        this.serverFollow.stop();
        this.serverFollow = null;
      }
    },
    /** the downloaded pdfs stay open-able through their blob urls until the render is dismissed */
    releaseServerFiles() {
      Object.values(this.serverDownloaded).forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
      this.serverDownloaded = {};
    },
    dismissServerRender() {
      this.releaseServerFiles();
      this.serverRender = null;
    },
    /**
     * Save each page's pdf as soon as the server has it: fetched through the api client
     * (which carries the login; the api keeps nothing public) and handed to the browser
     * under the macro's file name, opened in a new tab too when asked. A render delivered
     * to slack has nothing to download: the server posted the pdfs and kept none.
     */
    async downloadServerFiles() {
      const job = this.serverRender;
      if (job.delivery === 'slack') {
        return;
      }
      for (const step of job.steps) {
        if (!step.status.startsWith('done') || !step.file || step.index in this.serverDownloaded) {
          continue;
        }
        this.serverDownloaded = {...this.serverDownloaded, [step.index]: null};
        try {
          const pdf = await fetchRenderedPdf(job.id, step.index);
          const url = downloadBlob(pdf, step.filename, 'application/pdf', {keepUrl: true});
          this.serverDownloaded = {...this.serverDownloaded, [step.index]: url};
          if (this.openAfterSave && !openInNewTab(url)) {
            this.openBlocked++;
          }
        } catch (e) {
          this.notify(`could not download ${step.filename}: ${e.message || e}`, 'error');
        }
        this.clientMemoryBytes = browserMemoryUsed();
      }
    },
    /**
     * Where a page's pdf can be opened from: the copy downloaded here, else the file the
     * server posted to slack (a slack delivery keeps no file to download).
     */
    serverPdfLink(step) {
      if (this.serverDownloaded[step.index]) {
        return {href: this.serverDownloaded[step.index], title: 'open this pdf'};
      }
      const slack = this.serverRender && this.serverRender.slack;
      const posted = slack && slack.status === 'sent' && slack.files.find(f => f.filename === step.filename);
      if (posted && posted.permalink) {
        return {href: posted.permalink, title: `open this pdf on Slack ${slack.channel}`};
      }
      return null;
    },
    /** time, pdf size and the server's memory for a page the server printed */
    serverStepStats(step) {
      if (!step || ['queued', 'running'].includes(step.status)) {
        return '';
      }
      return pageStatsText({
        durationMs: step.durationMs,
        waitedMs: step.waitedMs,
        size: step.size,
        memory: [{label: 'server', bytes: step.memoryBytes}, {label: 'chrome', bytes: step.browserMemoryBytes}],
      });
    },
    finishServerRender() {
      const job = this.serverRender;
      const {macro, target} = this.serverRenderContext || {};
      if (!job || !macro) {
        return;
      }
      const results = job.steps.map(step => ({
        filename: step.filename,
        href: this.pageHref(step.href),
        status: step.status,
        error: step.error,
      }));
      // the links list goes with the downloads; a slack delivery has its files on slack
      if (job.delivery !== 'slack' && results.some(r => r.status.startsWith('done'))) {
        downloadBlob(replayLinksText({macroName: macro.name, target, results}), linksFilename(target, macro.name), 'text/plain');
      }
      const done = job.steps.filter(s => s.status.startsWith('done')).length;
      const failed = job.steps.filter(s => s.status === 'failed').length;
      const where = job.delivery === 'slack' ? (job.slack && job.slack.status === 'sent' ? `posted to Slack ${job.slack.channel}` : 'rendered') : 'saved';
      const summary = `server render ${job.status}: ${done} of ${job.steps.length} pages ${where}${failed ? `, ${failed} failed` : ''}`;
      if (this.openBlocked) {
        this.notify(`${summary}. The browser blocked opening ${this.openBlocked} of them: allow pop-ups for this site, or use the Open buttons`, 'warning');
      } else {
        this.notify(summary, failed || job.status !== 'done' ? 'warning' : 'success');
      }
    },
    async cancelServerRender() {
      if (!this.serverRender) {
        return;
      }
      try {
        await cancelRender(this.serverRender.id);
      } catch (e) {
        this.notify(`could not stop the render: ${e.message || e}`, 'error');
      }
    },
    serverStepColor(status) {
      return {done: 'success', 'done-unsettled': 'orange', failed: 'error', cancelled: 'grey', running: 'primary'}[status] || 'grey';
    },
    serverStepIcon(status) {
      return {
        done: 'mdi-check-circle', 'done-unsettled': 'mdi-alert-circle', failed: 'mdi-close-circle',
        cancelled: 'mdi-cancel', running: 'mdi-progress-clock',
      }[status] || 'mdi-circle-outline';
    },
    startEditing() {
      const macro = this.selectedMacro;
      this.draftError = null;
      this.draft = {
        name: macro.name,
        orientation: macro.orientation,
        delivery: macro.delivery,
        slackChannel: macro.slackChannel,
        schedule: this.editableSchedule(macro),
        steps: macro.steps.map((step, index) => ({
          key: `${index}-${step.path}`,
          original: step,
          label: step.label,
          url: stepUrl(step),
          filename: step.filename || slugify(step.label),
        })),
      };
    },
    moveDraftStep(index, direction) {
      const steps = this.draft.steps;
      const [step] = steps.splice(index, 1);
      steps.splice(index + direction, 0, step);
    },
    /** a draft step back into a saved step; a changed url is re-parsed and loses the recorded route */
    draftStepToStep(draftStep, index) {
      const {original, label, url, filename} = draftStep;
      const step = {...original, label: label.trim() || original.label, filename: cleanFilename(filename)};
      if (url.trim() === stepUrl(original)) {
        return step;
      }
      let parsed;
      try {
        parsed = parseStepUrl(url);
      } catch (e) {
        throw new Error(`page ${index + 1}: ${e.message}`);
      }
      // eslint-disable-next-line no-unused-vars
      const {route, ...withoutRoute} = step;
      return {...withoutRoute, ...parsed, warnings: urlWarnings(url)};
    },
    async saveDraft() {
      if (this.readOnly) {
        return;
      }
      const macro = this.selectedMacro;
      const name = this.draft.name.trim();
      if (!name) {
        this.draftError = 'the macro needs a name';
        return;
      }
      const problem = scheduleProblem(this.draft.schedule, this.draft);
      if (problem) {
        this.draftError = `schedule: ${problem}`;
        return;
      }
      let steps;
      try {
        steps = this.draft.steps.map((step, index) => this.draftStepToStep(step, index));
      } catch (e) {
        this.draftError = e.message;
        return;
      }
      let saved;
      try {
        saved = await this.updateMacro({id: macro.id, name, orientation: this.draft.orientation, delivery: this.draft.delivery, slackChannel: this.draft.slackChannel, schedule: this.draft.schedule, steps});
      } catch (e) {
        this.draftError = `could not save: ${(e.response && e.response.data && e.response.data.error) || e.message || e}`;
        return;
      }
      this.draft = null;
      this.draftError = null;
      // the url names the macro, so keep the link pointing at it
      this.playSlug = slugify(name);
      // the api takes an edited macro off its schedule: what was checked when it was set up no longer holds
      if (saved.scheduleCleared) {
        this.$store.commit('showSnackbar', {message: `saved "${name}"; its schedule is off because the macro changed - use Run on schedule to set it again`, color: 'warning', timeout: 8000});
      } else {
        this.notify(`saved "${name}"`, 'success');
      }
    },
    async confirmDelete(macro) {
      if (confirm(`Delete macro "${macro.name}"?`)) {
        try {
          await this.deleteMacro(macro.id);
          this.showList();
        } catch (e) {
          this.notify(`could not delete the macro: ${e.message || e}`, 'error');
        }
      }
    },
    exportMacros(macros, filename) {
      downloadBlob(JSON.stringify(macros, null, 2), filename, 'application/json');
    },
    importFile(event) {
      const [file] = event.target.files || [];
      event.target.value = '';
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const parsed = JSON.parse(reader.result);
          const count = await this.importMacros(parsed);
          this.notify(`imported ${count} macro${count === 1 ? '' : 's'}`, 'success');
        } catch (e) {
          alert(`could not import: ${e.message}`);
        }
      };
      reader.readAsText(file);
    },
  },
};
</script>

<style scoped>
code {
  font-size: 0.85em;
}

/* the editor's delivery selects: side by side with a little air between them, wrapping on a narrow column */
.macro-options > * {
  flex: 1 1 200px;
  max-width: 260px;
  margin: 4px 12px 4px 0;
}

.text-mono >>> input {
  font-family: monospace;
  font-size: 0.85em;
}
</style>
